import type { ApolloCache } from '@apollo/client';
import { isApolloError } from '@apollo/client';
import type { CacheFieldName } from '@risksmart-app/web-graphql-client/generated/graphql';
import { isTRPCClientError } from '@trpc/client';
import type { ZodType } from 'zod';

export type { CacheFieldName };

export const evictField = <T>(
  cache: ApolloCache<T>,
  fieldName: CacheFieldName
) => {
  cache.evict({
    id: 'ROOT_QUERY',
    fieldName: fieldName,
  });
  cache.gc();
};

export const isPermissionError = (e: unknown) => {
  return (
    e instanceof Error &&
    isApolloError(e) &&
    e.graphQLErrors.length > 0 &&
    e.graphQLErrors[0].extensions?.code === 'permission-error'
  );
};

export enum HasuraErrorCodes {
  ConstraintError = 'constraint-violation',
  PermissionError = 'permission-error',
  UnexpectedError = 'unexpected',
  ValidationFailed = 'validation-failed',
}

/**
 * Error accessors for the GraphQL→tRPC bridge (COI-577). Each takes `unknown`
 * and returns a plain value, never the Apollo error object, so call-sites stay
 * off Apollo-shaped access.
 *
 * There is deliberately no raw `cause` / `graphQLErrors` accessor:
 * `src/utils/formError.ts` owns the structured-cause contract and must not gain
 * a bypass route.
 *
 * All four return `undefined` for a plain `Error`; a call-site wanting a
 * fallback narrows it itself.
 */
const asApolloError = (e: unknown) =>
  e instanceof Error && isApolloError(e) ? e : undefined;

/** Every GraphQL error message joined, or the tRPC message. */
export const getAllErrorMessages = (e: unknown): string | undefined => {
  const apollo = asApolloError(e);
  if (apollo) {
    return apollo.graphQLErrors.map((error) => error.message).join(' ');
  }
  if (isTRPCClientError(e)) {
    return e.message;
  }

  return undefined;
};

/** The first GraphQL error message, or the tRPC message. */
export const getFirstErrorMessage = (e: unknown): string | undefined => {
  const apollo = asApolloError(e);
  if (apollo) {
    return apollo.graphQLErrors[0]?.message;
  }
  if (isTRPCClientError(e)) {
    return e.message;
  }

  return undefined;
};

/**
 * The first GraphQL error's `extensions.code`, or the tRPC error's data code.
 * A non-string Apollo code returns `undefined` rather than being cast.
 */
export const getErrorCode = (e: unknown): string | undefined => {
  const apollo = asApolloError(e);
  if (apollo) {
    const code = apollo.graphQLErrors[0]?.extensions?.code;

    return typeof code === 'string' ? code : undefined;
  }
  if (isTRPCClientError(e)) {
    return e.data?.code;
  }

  return undefined;
};

/**
 * The first GraphQL error's `extensions`, validated against a caller-supplied
 * schema so the call-site declares its expected shape without an `as` cast.
 *
 * Apollo only — the tRPC leg surfaces this as a structured cause read through
 * `formError.ts`.
 */
export const getErrorExtensions = <T>(
  e: unknown,
  schema: ZodType<T>
): T | undefined => {
  const apollo = asApolloError(e);
  if (!apollo) {
    return undefined;
  }
  const parsed = schema.safeParse(apollo.graphQLErrors[0]?.extensions);

  return parsed.success ? parsed.data : undefined;
};
